import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ContactAlerts, useContactSubmit } from '../services/contactService';

const UpgradeRequestDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const { submit, loading, error, sent, resetStatus } = useContactSubmit();
  const [formData, setFormData] = useState({
    licenseType: '',
    primaryUseCase: '',
    requestPricing: false,
    phoneCall: false,
    emailFollowup: false,
    phoneNumber: '',
    preferredTime: '',
    comments: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const resetForm = () => {
    setFormData({
      licenseType: '',
      primaryUseCase: '',
      requestPricing: false,
      phoneCall: false,
      emailFollowup: false,
      phoneNumber: '',
      preferredTime: '',
      comments: '',
    });
    resetStatus();
  };

  const handleSubmit = async () => {
    await submit({
      type: 'upgrade',
      ...formData,
      name: user?.user_metadata?.first_name && user?.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : undefined,
      email: user?.email,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Upgrade to Pro
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <ContactAlerts error={error} sent={sent} />
          {/* License Type */}
          <FormControl fullWidth>
            <InputLabel>License Type</InputLabel>
            <Select
              value={formData.licenseType}
              onChange={(e) => handleInputChange('licenseType', e.target.value)}
              label="License Type"
            >
              <MenuItem value="Individual User">Individual User - Single user license</MenuItem>
              <MenuItem value="Team License">Team License - 2-10 users</MenuItem>
              <MenuItem value="Enterprise">Enterprise - 10+ users with custom features</MenuItem>
            </Select>
          </FormControl>

          {/* Primary Use Case */}
          <FormControl fullWidth>
            <InputLabel>Primary Use Case</InputLabel>
            <Select
              value={formData.primaryUseCase}
              onChange={(e) => handleInputChange('primaryUseCase', e.target.value)}
              label="Primary Use Case"
            >
              <MenuItem value="Due Diligence">Due Diligence</MenuItem>
              <MenuItem value="Compliance">Compliance</MenuItem>
              <MenuItem value="Research">Research</MenuItem>
              <MenuItem value="Risk Assessment">Risk Assessment</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Pricing Request */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.requestPricing}
                onChange={(e) => handleCheckboxChange('requestPricing', e.target.checked)}
              />
            }
            label="Request pricing information"
          />

          {/* Contact Preferences */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Contact Preferences
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.phoneCall}
                  onChange={(e) => handleCheckboxChange('phoneCall', e.target.checked)}
                />
              }
              label="Phone"
            />
            
            {formData.phoneCall && (
              <Box sx={{ ml: 4, mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  placeholder="(555) 123-4567"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                />
                <FormControl fullWidth>
                  <InputLabel>Preferred Call Time</InputLabel>
                  <Select
                    value={formData.preferredTime}
                    onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                    label="Preferred Call Time"
                  >
                    <MenuItem value="Morning (8 AM - 12 PM)">Morning (8 AM - 12 PM)</MenuItem>
                    <MenuItem value="Afternoon (12 PM - 5 PM)">Afternoon (12 PM - 5 PM)</MenuItem>
                    <MenuItem value="Evening (5 PM - 8 PM)">Evening (5 PM - 8 PM)</MenuItem>
                    <MenuItem value="Flexible - Any time works">Flexible - Any time works</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailFollowup}
                  onChange={(e) => handleCheckboxChange('emailFollowup', e.target.checked)}
                />
              }
              label="Email"
            />
          </Box>

          {/* Comments - Last */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Additional Comments"
            placeholder="Please describe your specific needs, use cases, or any questions..."
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${formData.comments.length}/500 characters`}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>{sent ? 'Close' : 'Cancel'}</Button>
        {!sent && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeRequestDialog;
